import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Compass, Target, Lightbulb, Flame, CheckCircle, Plus, Edit2, Trash2, AlertCircle, Bell, Archive, RotateCcw, Flag } from 'lucide-react';
import ArchiveWarningDialog from '@/components/common/ArchiveWarningDialog';
import ArchivedParentBadge from '@/components/common/ArchivedParentBadge';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import ConfirmableDialog from '@/components/common/ConfirmableDialog';
import ObjectiveSearch from '../components/discovery/ObjectiveSearch';
import ObjectiveCard from '@/components/discovery/ObjectiveCard';
import SearchableTagSelect from '@/components/common/SearchableTagSelect';
import ObjectiveDiscoveryRedesign from '@/components/discovery/ObjectiveDiscoveryRedesign';
import ObjectiveDiscoveryFullPage from '@/components/discovery/ObjectiveDiscoveryFullPage';


export default function Discovery() {
  const queryClient = useQueryClient();
  
  // Read mission ID from URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const missionIdFromUrl = urlParams.get('mission');
  
  const [selectedMissionId, setSelectedMissionId] = useState(missionIdFromUrl);
  const [objectiveDialog, setObjectiveDialog] = useState(false);
  const [editingObjective, setEditingObjective] = useState(null);

  const [objectiveForm, setObjectiveForm] = useState({
    statement: '',
    specific: '',
    measurable: '',
    attainable: '',
    relevant: '',
    time_bound: '',
    iteration_notes: '',
    bridge_quality_score: undefined,
    smart_completeness_score: undefined,
    smart_specific_score: undefined,
    smart_measurable_score: undefined,
    smart_attainable_score: undefined,
    smart_relevant_score: undefined,
    smart_time_bound_score: undefined
  });

  // AI-Assisted Discovery State
  const [conversationMessages, setConversationMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [needsEvaluation, setNeedsEvaluation] = useState(false);
  const [scores, setScores] = useState({
    bridge_quality: 0,
    smart_completeness: 0,
    specific_score: 0,
    measurable_score: 0,
    attainable_score: 0,
    relevant_score: 0,
    timebound_score: 0
  });
  const [currentObjectiveDraft, setCurrentObjectiveDraft] = useState({
    statement: '',
    specific: '',
    measurable: '',
    attainable: '',
    relevant: '',
    time_bound: ''
  });

  // Advisor Settings State
  const [showAdvisorSettings, setShowAdvisorSettings] = useState(false);
  const [advisorTone, setAdvisorTone] = useState('direct');
  const [customTone, setCustomTone] = useState('');
  const [customOpeningStatement, setCustomOpeningStatement] = useState('');
  const [qualityMode, setQualityMode] = useState('balanced');
  const [filteredObjectives, setFilteredObjectives] = useState([]);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [objectiveToArchive, setObjectiveToArchive] = useState(null);
  const [archiveDependents, setArchiveDependents] = useState([]);

  const { data: user } = useQuery({
    queryKey: ['me'],
    queryFn: () => base44.auth.me()
  });

  const { data: profile } = useQuery({
    queryKey: ['myProfile'],
    queryFn: async () => {
      const profiles = await base44.entities.OfficerProfile.list();
      return profiles.find((p) => p.user_email === user?.email);
    },
    enabled: !!user
  });

  const isCommander = user?.role === 'admin' || profile?.custom_role === 'Commander';

  const canActivateObjective = (objective) => {
    if (!selectedMission) return false;
    const isMissionOwner = selectedMission.owner_email === user?.email;
    const isMissionSponsor = selectedMission.sponsor_email === user?.email;
    return isCommander && (isMissionOwner || isMissionSponsor);
  };

  const { data: missions } = useQuery({
    queryKey: ['myMissions', user?.email],
    queryFn: async () => {
      const allMissions = await base44.entities.Mission.list();
      return allMissions
        .filter((m) =>
          m.assigned_to_email === user?.email ||
          m.sponsor_email === user?.email ||
          m.owner_email === user?.email ||
          (m.affiliated_officers || []).includes(user?.email)
        )
        .sort((a, b) => (b.xp_reward || 0) - (a.xp_reward || 0));
    },
    enabled: !!user,
    initialData: []
  });

  const { data: objectives } = useQuery({
    queryKey: ['objectives', selectedMissionId],
    queryFn: () => base44.entities.Objective.filter({ mission_id: selectedMissionId }),
    enabled: !!selectedMissionId,
    initialData: []
  });

  const { data: allObjectives } = useQuery({
    queryKey: ['allObjectives'],
    queryFn: () => base44.entities.Objective.list(),
    initialData: []
  });

  const { data: officers } = useQuery({
    queryKey: ['officers'],
    queryFn: () => base44.entities.OfficerProfile.list(),
    initialData: []
  });

  const { data: activities } = useQuery({
    queryKey: ['activities'],
    queryFn: () => base44.entities.Activity.list(),
    initialData: []
  });

  const { data: dailyTasks } = useQuery({
    queryKey: ['dailyTasks'],
    queryFn: () => base44.entities.DailyTask.list(),
    initialData: []
  });

  const { data: vocabRecords } = useQuery({
    queryKey: ['bullseyeVocabulary'],
    queryFn: () => base44.entities.BullseyeVocabulary.list(),
    initialData: []
  });

  const measurements = (vocabRecords || []).filter(v => v.type === 'measurement' && v.is_active).sort((a, b) => a.sort_order - b.sort_order);
  const positions = (vocabRecords || []).filter(v => v.type === 'position' && v.is_active).sort((a, b) => a.sort_order - b.sort_order);
  const measurementCategories = [...new Set(measurements.map(m => m.category))];
  const positionCategories = [...new Set(positions.map(p => p.category))];

  const [objectiveTarget, setObjectiveTarget] = useState({
    target_measurement_id: '',
    target_value: '',
    target_unit: '',
    target_position_type: '',
    target_position_value: '',
    target_deadline: ''
  });

  const [positionSuggestions, setPositionSuggestions] = useState([]);
  useEffect(() => {
    if (!objectiveTarget.target_position_type) { setPositionSuggestions([]); return; }
    base44.functions.invoke('bullseyePositionValues', { position_type: objectiveTarget.target_position_type })
      .then(res => { setPositionSuggestions(res?.values || res?.data?.values || []); })
      .catch(() => { setPositionSuggestions([]); });
  }, [objectiveTarget.target_position_type]);

  const createObjectiveMutation = useMutation({
    mutationFn: async (data) => {
      // Generate objective code
      const mission = missions.find(m => m.id === selectedMissionId);
      const missionObjectives = objectives.filter(o => o.mission_id === selectedMissionId);
      const objective_code = `${mission?.mission_code || 'UNK'}-O${missionObjectives.length + 1}`;
      
      return base44.entities.Objective.create({
        ...data,
        objective_code,
        mission_id: selectedMissionId,
        owner_email: user?.email,
        status: 'pending_activation',
        activation_requested_by: user?.email,
        activation_requested_at: new Date().toISOString()
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objectives'] });
      setObjectiveForm({
        statement: '',
        specific: '',
        measurable: '',
        attainable: '',
        relevant: '',
        time_bound: '',
        iteration_notes: '',
        bridge_quality_score: undefined,
        smart_completeness_score: undefined,
        smart_specific_score: undefined,
        smart_measurable_score: undefined,
        smart_attainable_score: undefined,
        smart_relevant_score: undefined,
        smart_time_bound_score: undefined
      });
      // Dialog close is now handled by finalizeObjective with a 5s delay
      // so the user sees the sparkle + finalized card
    }
  });

  const updateObjectiveMutation = useMutation({
    mutationFn: async ({ id, data, oldStatement }) => {
      // Append modification history to iteration_notes
      const timestamp = new Date().toISOString();
      const modificationEntry = `\n\n[MODIFIED ${format(new Date(timestamp), 'MMM d, yyyy HH:mm')}]\nOld Statement: ${oldStatement}`;
      const updatedIterationNotes = (data.iteration_notes || '') + modificationEntry;
      
      // Set status back to pending_activation to trigger approval
      return base44.entities.Objective.update(id, {
        ...data,
        iteration_notes: updatedIterationNotes,
        status: 'pending_activation',
        activation_requested_by: user?.email,
        activation_requested_at: timestamp
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objectives'] });
      setObjectiveForm({
        statement: '',
        specific: '',
        measurable: '',
        attainable: '',
        relevant: '',
        time_bound: '',
        iteration_notes: '',
        bridge_quality_score: undefined,
        smart_completeness_score: undefined,
        smart_specific_score: undefined,
        smart_measurable_score: undefined,
        smart_attainable_score: undefined,
        smart_relevant_score: undefined,
        smart_time_bound_score: undefined
      });
      // Dialog close is now handled by finalizeObjective with a 5s delay
    }
  });

  const deleteObjectiveMutation = useMutation({
    mutationFn: async (id) => {
      const objectiveActivities = activities.filter(a => a.objective_id === id);
      if (objectiveActivities.length > 0) {
        throw new Error('Cannot delete objective with existing activities');
      }
      return base44.entities.Objective.delete(id);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['objectives'] })
  });

  const archiveObjectiveMutation = useMutation({
    mutationFn: (id) => base44.entities.Objective.update(id, { status: 'archived' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objectives'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    }
  });

  const unarchiveObjectiveMutation = useMutation({
    mutationFn: (id) => base44.entities.Objective.update(id, { 
      status: 'pending_activation',
      activation_requested_by: user?.email,
      activation_requested_at: new Date().toISOString()
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['objectives'] })
  });

  const updateObjectiveStatusFlagMutation = useMutation({
    mutationFn: ({ id, status_flag }) => base44.entities.Objective.update(id, { status_flag }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['objectives'] })
  });

  const activateObjectiveMutation = useMutation({
    mutationFn: async (id) => {
      const objective = objectives.find(o => o.id === id);
      const mission = missions.find(m => m.id === objective?.mission_id);
      const missionCode = mission?.mission_code || 'UNK';

      // Get all objectives for this mission to find the highest existing code number
      const allMissionObjectives = allObjectives.filter(o => o.mission_id === objective?.mission_id && o.id !== id);
      let maxNum = 0;
      allMissionObjectives.forEach(o => {
        const match = o.objective_code?.match(/-O(\d+)$/);
        if (match) maxNum = Math.max(maxNum, parseInt(match[1], 10));
      });
      const uniqueCode = `${missionCode}-O${maxNum + 1}`;

      return base44.entities.Objective.update(id, { 
        status: 'active',
        objective_code: uniqueCode,
        activation_approved_by: user?.email,
        activation_approved_at: new Date().toISOString()
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objectives'] });
      queryClient.invalidateQueries({ queryKey: ['allObjectives'] });
    }
  });

  const requestActivationMutation = useMutation({
    mutationFn: (id) => base44.entities.Objective.update(id, { 
      status: 'pending_activation',
      activation_requested_by: user?.email,
      activation_requested_at: new Date().toISOString()
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['objectives'] })
  });

  const pingCommandersMutation = useMutation({
    mutationFn: async ({ objectiveId }) => {
      const objective = objectives.find(o => o.id === objectiveId);
      if (!objective || !selectedMission) return;
      
      const emails = [selectedMission.sponsor_email, selectedMission.owner_email].filter(Boolean);
      
      for (const email of emails) {
        await base44.integrations.Core.SendEmail({
          to: email,
          subject: `Objective Activation Request: ${selectedMission.title}`,
          body: `An officer has requested activation for an objective:\n\n"${objective.statement}"\n\nPlease review and approve in your Command Deck.`
        });
      }
    }
  });

  const getToneInstructions = () => {
    const toneStyles = {
      direct: 'Keep responses brief (1-2 sentences). Use direct language. Avoid subjective terms.',
      socratic: 'Ask probing questions to help the officer discover insights. Guide through inquiry rather than instruction.',
      supportive: 'Use encouraging and empowering language. Acknowledge progress and build confidence while guiding improvement.',
      analytical: 'Provide data-driven feedback with specific metrics. Reference frameworks and best practices explicitly.'
    };

    if (advisorTone === 'custom' && customTone) {
      return customTone;
    }
    return toneStyles[advisorTone] || toneStyles.direct;
  };

  const startAiDiscovery = async () => {
    setIsAiThinking(true);
    try {
      const response = await base44.functions.invoke('discoveryChat', {
        mode: 'initial',
        mission: { title: selectedMission.title, commanders_intent: selectedMission.commanders_intent, briefing_text: selectedMission.briefing_text, objective: selectedMission.objective },
        profile: { mastery: profile?.mastery || [], autonomy: profile?.autonomy || [], purpose: profile?.purpose || [] },
        conversation_history: [],
        custom_opening_statement: customOpeningStatement || null,
        tone_preference: getToneInstructions(),
        quality_mode: qualityMode
      });
      setConversationMessages([{ 
        role: 'assistant', 
        content: response.data.advisor_response,
        suggestions: [
          "I want to own the outcome end-to-end",
          "I see a gap I can close",
          "This connects to something I want to master"
        ]
      }]);
    } catch (error) {
      console.error('AI Discovery failed:', error);
    } finally {
      setIsAiThinking(false);
    }
  };

  const handleUserMessage = async (text) => {
    const messageText = text || userInput;
    if (!messageText.trim()) return;

    const newMessages = [
    ...conversationMessages,
    { role: 'user', content: messageText }];

    setConversationMessages(newMessages);
    setUserInput('');
    setIsAiThinking(true);

    try {
      const evaluation = await base44.functions.invoke('discoveryChat', {
        mode: 'message',
        mission: { title: selectedMission.title, commanders_intent: selectedMission.commanders_intent, briefing_text: selectedMission.briefing_text, objective: selectedMission.objective },
        profile: { mastery: profile?.mastery || [], autonomy: profile?.autonomy || [], purpose: profile?.purpose || [] },
        conversation_history: newMessages,
        user_input: messageText,
        tone_preference: getToneInstructions(),
        quality_mode: qualityMode
      });

      setConversationMessages([
      ...newMessages,
      { role: 'assistant', content: evaluation.data.advisor_response }]
      );

      setScores({
        bridge_quality: evaluation.data.bridge_quality,
        smart_completeness: evaluation.data.smart_completeness,
        specific_score: evaluation.data.specific_score,
        measurable_score: evaluation.data.measurable_score,
        attainable_score: evaluation.data.attainable_score,
        relevant_score: evaluation.data.relevant_score,
        timebound_score: evaluation.data.timebound_score
      });

      setCurrentObjectiveDraft(evaluation.data.extracted_objective);
      setNeedsEvaluation(false);

    } catch (error) {
      console.error('AI conversation failed:', error);
      setConversationMessages([
      ...newMessages,
      { role: 'assistant', content: 'I encountered an issue. Could you rephrase that?' }]
      );
    } finally {
      setIsAiThinking(false);
    }
  };

  const evaluateObjective = async () => {
    setIsAiThinking(true);
    try {
      const evaluation = await base44.functions.invoke('discoveryChat', {
        mode: 'evaluate',
        mission: { title: selectedMission.title, commanders_intent: selectedMission.commanders_intent, briefing_text: selectedMission.briefing_text, objective: selectedMission.objective },
        profile: { mastery: profile?.mastery || [], autonomy: profile?.autonomy || [], purpose: profile?.purpose || [] },
        conversation_history: conversationMessages,
        current_objective_draft: currentObjectiveDraft,
        tone_preference: getToneInstructions(),
        quality_mode: qualityMode
      });

      setScores({
        bridge_quality: evaluation.data.bridge_quality,
        smart_completeness: evaluation.data.smart_completeness,
        specific_score: evaluation.data.specific_score,
        measurable_score: evaluation.data.measurable_score,
        attainable_score: evaluation.data.attainable_score,
        relevant_score: evaluation.data.relevant_score,
        timebound_score: evaluation.data.timebound_score
      });

      setCurrentObjectiveDraft(evaluation.data.extracted_objective);
      setNeedsEvaluation(false);
    } catch (error) {
      console.error('Evaluation failed:', error);
    } finally {
      setIsAiThinking(false);
    }
  };

  const finalizeObjective = async (editedStatement) => {
    // Use the edited statement if provided, otherwise fall back to the draft
    const finalDraft = editedStatement && editedStatement !== currentObjectiveDraft.statement
      ? { ...currentObjectiveDraft, statement: editedStatement }
      : currentObjectiveDraft;

    const targetFields = {
      target_measurement_id: objectiveTarget.target_measurement_id || undefined,
      target_value: objectiveTarget.target_value ? parseFloat(objectiveTarget.target_value) : undefined,
      target_unit: objectiveTarget.target_unit || undefined,
      target_position_type: objectiveTarget.target_position_type || undefined,
      target_position_value: objectiveTarget.target_position_value || undefined,
      target_deadline: objectiveTarget.target_deadline || undefined,
    };

    if (editingObjective) {
      // Update existing objective
      updateObjectiveMutation.mutate({
        id: editingObjective.id,
        data: {
          ...finalDraft,
          ...targetFields,
          iteration_notes: conversationMessages.map((m) =>
            `${m.role === 'user' ? 'Officer' : 'Advisor'}: ${m.content}`
          ).join('\n\n'),
          bridge_quality_score: scores.bridge_quality,
          smart_completeness_score: scores.smart_completeness
        },
        oldStatement: editingObjective.statement
      });
    } else {
      // Generate objective code for new objective
      const mission = missions.find(m => m.id === selectedMissionId);
      const missionObjectives = objectives.filter(o => o.mission_id === selectedMissionId);
      const objective_code = `${mission?.mission_code || 'UNK'}-O${missionObjectives.length + 1}`;
      
      createObjectiveMutation.mutate({
        ...finalDraft,
        ...targetFields,
        objective_code,
        iteration_notes: conversationMessages.map((m) =>
          `${m.role === 'user' ? 'Officer' : 'Advisor'}: ${m.content}`
        ).join('\n\n'),
        bridge_quality_score: scores.bridge_quality,
        smart_completeness_score: scores.smart_completeness
      });
    }

    // Dialog close is handled by the 30-second countdown inside ObjectiveDiscoveryFullPage
    // which triggers onClose → setObjectiveDialog(false) + resetDiscovery()
  };

  const resetDiscovery = () => {
    setConversationMessages([]);
    setUserInput('');
    setNeedsEvaluation(false);
    setScores({
      bridge_quality: 0,
      smart_completeness: 0,
      specific_score: 0,
      measurable_score: 0,
      attainable_score: 0,
      relevant_score: 0,
      timebound_score: 0
    });
    setCurrentObjectiveDraft({
      statement: '',
      specific: '',
      measurable: '',
      attainable: '',
      relevant: '',
      time_bound: ''
    });
    setObjectiveTarget({
      target_measurement_id: '',
      target_value: '',
      target_unit: '',
      target_position_type: '',
      target_position_value: '',
      target_deadline: ''
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingObjective) {
      updateObjectiveMutation.mutate({ 
        id: editingObjective.id, 
        data: objectiveForm,
        oldStatement: editingObjective.statement 
      });
    } else {
      createObjectiveMutation.mutate(objectiveForm);
    }
  };

  const handleEdit = (objective) => {
    setEditingObjective(objective);
    setObjectiveForm({
      statement: objective.statement,
      specific: objective.specific || '',
      measurable: objective.measurable || '',
      attainable: objective.attainable || '',
      relevant: objective.relevant || '',
      time_bound: objective.time_bound || '',
      iteration_notes: objective.iteration_notes || '',
      bridge_quality_score: objective.bridge_quality_score,
      smart_completeness_score: objective.smart_completeness_score,
      smart_specific_score: objective.smart_specific_score,
      smart_measurable_score: objective.smart_measurable_score,
      smart_attainable_score: objective.smart_attainable_score,
      smart_relevant_score: objective.smart_relevant_score,
      smart_time_bound_score: objective.smart_time_bound_score
    });
    
    // Pre-populate AI discovery state with existing objective data
    setCurrentObjectiveDraft({
      statement: objective.statement || '',
      specific: objective.specific || '',
      measurable: objective.measurable || '',
      attainable: objective.attainable || '',
      relevant: objective.relevant || '',
      time_bound: objective.time_bound || ''
    });
    
    // Pre-populate scores if they exist
    if (objective.bridge_quality_score !== undefined || objective.smart_completeness_score !== undefined) {
      setScores({
        bridge_quality: objective.bridge_quality_score || 0,
        smart_completeness: objective.smart_completeness_score || 0,
        specific_score: objective.specific ? 100 : 0,
        measurable_score: objective.measurable ? 100 : 0,
        attainable_score: objective.attainable ? 100 : 0,
        relevant_score: objective.relevant ? 100 : 0,
        timebound_score: objective.time_bound ? 100 : 0
      });
    }
    
    // Pre-populate conversation with existing iteration notes if available
    if (objective.iteration_notes) {

    // Pre-populate Bullseye target fields if they exist
    setObjectiveTarget({
      target_measurement_id: objective.target_measurement_id || '',
      target_value: objective.target_value || '',
      target_unit: objective.target_unit || '',
      target_position_type: objective.target_position_type || '',
      target_position_value: objective.target_position_value || '',
      target_deadline: objective.target_deadline || ''
    });
      const parsedMessages = objective.iteration_notes.split('\n\n').map(line => {
        const [role, content] = line.split(': ');
        return {
          role: role === 'Officer' ? 'user' : 'assistant',
          content: content || line
        };
      });
      setConversationMessages(parsedMessages);
    }
    
    setObjectiveDialog(true);
  };

  const handleArchiveClick = async (objective) => {
    const objectiveActivities = activities.filter(a => a.objective_id === objective.id);
    const deps = [];
    
    objectiveActivities.forEach(act => {
      deps.push({
        type: 'Activity',
        name: act.title,
        code: act.activity_code,
        page: 'activities'
      });
      
      // Get tasks for this activity
      const activityTasks = dailyTasks.filter(t => t.activity_id === act.id);
      activityTasks.forEach(task => {
        deps.push({
          type: 'Daily Task',
          name: task.task_description,
          code: format(new Date(task.date), 'MMM d, yyyy'),
          page: 'dailytasks'
        });
      });
    });
    
    setArchiveDependents(deps);
    setObjectiveToArchive(objective);
    setArchiveDialogOpen(true);
  };

  const handleArchiveConfirm = () => {
    if (objectiveToArchive) {
      archiveObjectiveMutation.mutate(objectiveToArchive.id);
    }
  };

  const handleDeleteClick = async (objective) => {
    const objectiveActivities = activities.filter(a => a.objective_id === objective.id);
    if (objectiveActivities.length > 0) {
      alert(`Cannot delete objective with ${objectiveActivities.length} existing activit${objectiveActivities.length === 1 ? 'y' : 'ies'}. Please delete or archive activities first.`);
      return;
    }
    if (confirm('Delete this objective permanently? This action cannot be undone.')) {
      deleteObjectiveMutation.mutate(objective.id);
    }
  };

  const selectedMission = missions.find((m) => m.id === selectedMissionId);
  const smartComplete = objectiveForm.statement && objectiveForm.specific && objectiveForm.measurable &&
  objectiveForm.attainable && objectiveForm.relevant && objectiveForm.time_bound;

  // If discovery is open, render full-page view instead of the normal page
  if (objectiveDialog && selectedMission) {
    return (
      <ObjectiveDiscoveryFullPage
        onClose={() => {
          setObjectiveDialog(false);
          setEditingObjective(null);
          resetDiscovery();
        }}
        missionContext={{
          title: selectedMission?.title,
          commanders_intent: selectedMission?.commanders_intent,
          briefing_text: selectedMission?.briefing_text,
          objective: selectedMission?.objective,
        }}
        userDrive={{
          autonomy: profile?.autonomy || [],
          mastery: profile?.mastery || [],
          purpose: profile?.purpose || [],
        }}
        conversationMessages={conversationMessages}
        scores={scores}
        currentObjectiveDraft={currentObjectiveDraft}
        isLoading={isAiThinking}
        isCommander={isCommander}
        onSendMessage={handleUserMessage}
        onFinalizeObjective={finalizeObjective}
        onReset={resetDiscovery}
        onStartDiscovery={startAiDiscovery}
        targetFields={objectiveTarget}
        onTargetChange={setObjectiveTarget}
        measurements={measurements}
        positions={positions}
        measurementCategories={measurementCategories}
        positionCategories={positionCategories}
        positionSuggestions={positionSuggestions}
      />
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-white mb-2">Objective Discovery</h1>
        <p className="text-slate-400">Bridge commander's intent to your personal drive through SMART objectives</p>
      </div>

      {/* Mission Selector */}
      <Card className="glass-panel border-none">
        <CardContent className="p-6">
          <Label className="text-slate-700 mb-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 block">Select Your Mission</Label>
          <div className="flex items-center gap-2 text-xs mb-3">
            <span className="text-muted-foreground">AI Quality:</span>
            {[
              { value: 'fast', label: '⚡ Fast', desc: 'Quick iterations (Haiku — cheapest)' },
              { value: 'balanced', label: '⚖️ Balanced', desc: 'Best for most work (Sonnet)' },
              { value: 'deep', label: '🧠 Deep', desc: 'Critical objectives (Opus)' }
            ].map(opt => (
              <button key={opt.value} onClick={() => setQualityMode(opt.value)}
                className={`px-2 py-1 rounded text-xs transition-colors ${qualityMode === opt.value ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
                title={opt.desc}>
                {opt.label}
              </button>
            ))}
          </div>
          <SearchableTagSelect
            options={missions.map(m => ({ 
              value: m.id, 
              label: `[${m.mission_code}] ${m.title} (${m.game_mode.replace(/_/g, ' ')}) XP ${m.xp_reward}` 
            }))}
            value={selectedMissionId || ''}
            onValueChange={setSelectedMissionId}
            placeholder="Choose a mission to discover objectives..."
            emptyMessage="No missions available"
            className="bg-slate-950 border-slate-800 text-slate-100"
          />
        </CardContent>
      </Card>

      {selectedMission &&
      <AnimatePresence mode="wait">
          <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6">

            {/* Context Display */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Commander's Intent & Theater of War */}
              <Card className="glass-panel border-none">
                <CardHeader>
                  <CardTitle className="text-slate-700 text-lg font-semibold tracking-tight flex items-center gap-2">
                    <Target className="w-5 h-5 text-indigo-400" />
                    Mission Context
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {selectedMission.commanders_intent &&
                <div>
                      <h4 className="text-slate-700 mb-2 text-sm font-semibold">Commander's Intent</h4>
                      <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap">
                        {selectedMission.commanders_intent}
                      </p>
                    </div>
                }
                  
                  {selectedMission.briefing_text &&
                <div>
                      <h4 className="text-slate-700 mb-2 text-sm font-semibold">Theater of War</h4>
                      <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap">
                        {selectedMission.briefing_text}
                      </p>
                    </div>
                }

                  {!selectedMission.commanders_intent && !selectedMission.briefing_text &&
                <p className="text-slate-500 text-sm italic">No context available for this mission</p>
                }
                </CardContent>
              </Card>

              {/* Officer's Drive */}
              <Card className="glass-panel border-none">
                <CardHeader>
                  <CardTitle className="text-slate-700 text-lg font-semibold tracking-tight flex items-center gap-2">
                    <Flame className="w-5 h-5 text-orange-400" />
                    Your Drive
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(profile?.mastery || []).length > 0 &&
                <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Lightbulb className="w-4 h-4 text-amber-400" />
                        <h4 className="text-slate-700 text-sm font-semibold">Mastery</h4>
                      </div>
                      <ul className="space-y-1">
                        {profile.mastery.map((item, i) =>
                    <li key={i} className="text-slate-600 text-sm">• {item}</li>
                    )}
                      </ul>
                    </div>
                }

                  {(profile?.autonomy || []).length > 0 &&
                <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Compass className="w-4 h-4 text-cyan-400" />
                        <h4 className="text-slate-700 text-sm font-semibold">Autonomy</h4>
                      </div>
                      <ul className="space-y-1">
                        {profile.autonomy.map((item, i) =>
                    <li key={i} className="text-slate-600 text-sm">• {item}</li>
                    )}
                      </ul>
                    </div>
                }

                  {(profile?.purpose || []).length > 0 &&
                <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="w-4 h-4 text-purple-400" />
                        <h4 className="text-slate-700 text-sm font-semibold">Purpose</h4>
                      </div>
                      <ul className="space-y-1">
                        {profile.purpose.map((item, i) =>
                    <li key={i} className="text-slate-600 text-sm">• {item}</li>
                    )}
                      </ul>
                    </div>
                }

                  {!profile?.mastery?.length && !profile?.autonomy?.length && !profile?.purpose?.length &&
                <p className="text-slate-500 text-sm italic">
                      Define your drive in your Officer Profile to guide objective discovery
                    </p>
                }
                </CardContent>
              </Card>
            </div>

            {/* Search and Filters */}
            <ObjectiveSearch
              objectives={objectives}
              missions={missions}
              officers={officers}
              onFilteredResults={setFilteredObjectives}
            />

            {/* Discovered Objectives */}
            <Card className="glass-panel border-none">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-slate-700 text-lg font-semibold tracking-tight flex items-center gap-2">
                  <Compass className="w-5 h-5 text-indigo-400" />
                  Discovered Objectives ({filteredObjectives.length})
                </CardTitle>
              <div className="flex items-center gap-2">
                  {isCommander &&
                    <Dialog open={showAdvisorSettings} onOpenChange={setShowAdvisorSettings}>
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-slate-400 hover:text-slate-200">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>Strategic Advisor Settings</DialogTitle>
                          <p className="text-sm text-slate-400 mt-2">Customize how the advisor communicates</p>
                        </DialogHeader>
                        <div className="space-y-6 mt-4">
                          <div className="space-y-3">
                            <Label>Advisor Tone & Style</Label>
                            <div className="space-y-2">
                              {[
                                { value: 'direct', label: 'Direct', description: 'Brief, straightforward guidance' },
                                { value: 'socratic', label: 'Socratic', description: 'Question-based discovery approach' },
                                { value: 'supportive', label: 'Supportive', description: 'Encouraging and empowering' },
                                { value: 'analytical', label: 'Analytical', description: 'Data-driven and framework-focused' }
                              ].map(({ value, label, description }) => (
                                <div
                                  key={value}
                                  onClick={() => setAdvisorTone(value)}
                                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                    advisorTone === value
                                      ? 'border-indigo-500 bg-indigo-500/10'
                                      : 'border-slate-700 hover:border-slate-600'
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                      advisorTone === value ? 'border-indigo-500' : 'border-slate-600'
                                    }`}>
                                      {advisorTone === value && <div className="w-2 h-2 rounded-full bg-indigo-500" />}
                                    </div>
                                    <div>
                                      <div className="font-medium text-slate-200">{label}</div>
                                      <div className="text-xs text-slate-400">{description}</div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                              <div
                                onClick={() => setAdvisorTone('custom')}
                                className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                  advisorTone === 'custom'
                                    ? 'border-indigo-500 bg-indigo-500/10'
                                    : 'border-slate-700 hover:border-slate-600'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                                    advisorTone === 'custom' ? 'border-indigo-500' : 'border-slate-600'
                                  }`}>
                                    {advisorTone === 'custom' && <div className="w-2 h-2 rounded-full bg-indigo-500" />}
                                  </div>
                                  <div className="flex-1 space-y-2">
                                    <div>
                                      <div className="font-medium text-slate-200">Custom</div>
                                      <div className="text-xs text-slate-400">Define your own style</div>
                                    </div>
                                    {advisorTone === 'custom' && (
                                      <Textarea
                                        value={customTone}
                                        onChange={(e) => setCustomTone(e.target.value)}
                                        placeholder="e.g., Use military terminology and emphasize tactical precision..."
                                        className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 text-sm"
                                        rows={3}
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Custom Opening Statement</Label>
                            <p className="text-xs text-slate-500">Override the default advisor introduction</p>
                            <Textarea
                              value={customOpeningStatement}
                              onChange={(e) => setCustomOpeningStatement(e.target.value)}
                              placeholder="Leave empty to use default opening statement..."
                              className="bg-slate-950 border-slate-800 text-sm"
                              rows={8}
                            />
                          </div>
                          <Button
                            onClick={() => setShowAdvisorSettings(false)}
                            className="w-full bg-indigo-600 hover:bg-indigo-700"
                          >
                            Save Settings
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  }
                  <Button
                    className="bg-indigo-600 hover:bg-indigo-700"
                    onClick={() => {
                      setObjectiveDialog(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Discover New Objective
                  </Button>
                </div>
              </CardHeader>

              <CardContent>
                {filteredObjectives.length === 0 ?
              <div className="text-center py-12 border border-dashed border-slate-800 rounded-xl">
                    <Compass className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-500 mb-4">
                      {objectives.length === 0 ? 'No objectives discovered yet' : 'No objectives match your filters'}
                    </p>
                    {objectives.length === 0 && (
                      <p className="text-sm text-slate-600">Bridge mission intent to your drive by discovering your first objective</p>
                    )}
                  </div> :

              <div className="space-y-4">
                    {filteredObjectives.map((objective) => (
                      <ObjectiveCard
                        key={objective.id}
                        objective={objective}
                        mission={missions.find(m => m.id === objective.mission_id)}
                        officers={officers}
                        activities={activities}
                        dailyTasks={dailyTasks}
                        user={user}
                        profile={profile}
                        onEdit={handleEdit}
                        onArchive={handleArchiveClick}
                        onUnarchive={(id) => unarchiveObjectiveMutation.mutate(id)}
                        onDelete={handleDeleteClick}
                        onUpdateStatusFlag={(id, status_flag) => updateObjectiveStatusFlagMutation.mutate({ id, status_flag })}
                        onActivate={(id) => activateObjectiveMutation.mutate(id)}
                        onPingCommanders={(id) => pingCommandersMutation.mutate({ objectiveId: id })}
                        canActivate={canActivateObjective(objective)}
                      />
                    ))}
                  </div>
              }
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>
      }

      {!selectedMissionId && missions.length > 0 &&
      <Card className="glass-panel border-none">
          <CardContent className="py-12 text-center">
            <Compass className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 text-lg">Select a mission above to begin discovering objectives</p>
          </CardContent>
        </Card>
      }

      <ArchiveWarningDialog
        open={archiveDialogOpen}
        onClose={() => setArchiveDialogOpen(false)}
        onConfirm={handleArchiveConfirm}
        entityType="Objective"
        entityName={objectiveToArchive?.statement}
        dependents={archiveDependents}
      />
    </div>);

}